/**
 * Typed HTTP client for the RAM-OS engine.
 *
 * - Reads `VITE_ENGINE_BASE_URL` from `import.meta.env` (default
 *   `http://localhost:9171`, matching the engine's dev port).
 * - Sends `credentials: "include"` so the Auth.js session cookie set
 *   by the engine is attached on cross-origin requests.
 * - JSON in/out; throws `HttpError` (carrying `status` and parsed
 *   `body`) on non-2xx so callers can branch on the engine's error
 *   shape without re-parsing.
 * - Framework-free: no React, no router, no query-client coupling.
 */

const DEFAULT_BASE_URL = "http://localhost:9171"

const envBase = import.meta.env.VITE_ENGINE_BASE_URL
const rawBase = typeof envBase === "string" && envBase.length > 0 ? envBase : DEFAULT_BASE_URL

// Strip a single trailing slash so `${baseUrl}${path}` (path leading
// with `/`) joins cleanly without double slashes.
const baseUrl = rawBase.endsWith("/") ? rawBase.slice(0, -1) : rawBase

export class HttpError extends Error {
	readonly status: number
	readonly body: unknown

	constructor(status: number, body: unknown, message?: string) {
		super(message ?? `HTTP ${status}`)
		this.name = "HttpError"
		this.status = status
		this.body = body
	}
}

export interface HttpRequestOptions {
	method?: string
	signal?: AbortSignal
	headers?: Record<string, string>
	json?: unknown
}

const isJsonResponse = (res: Response): boolean => {
	const ct = res.headers.get("content-type")
	return ct?.toLowerCase().includes("application/json") ?? false
}

const parseBody = async (res: Response): Promise<unknown> => {
	if (res.status === 204) return undefined
	if (isJsonResponse(res)) {
		try {
			return await res.json()
		} catch {
			return undefined
		}
	}
	try {
		return await res.text()
	} catch {
		return undefined
	}
}

export const http = async <T>(path: string, options: HttpRequestOptions = {}): Promise<T> => {
	const { method = "GET", signal, headers: extraHeaders, json } = options

	const headers: Record<string, string> = {
		Accept: "application/json",
		...extraHeaders,
	}

	const init: RequestInit = {
		method,
		credentials: "include",
		headers,
	}
	if (signal !== undefined) init.signal = signal
	if (json !== undefined) {
		headers["Content-Type"] = "application/json"
		init.body = JSON.stringify(json)
	}

	const res = await fetch(`${baseUrl}${path}`, init)
	const parsed = await parseBody(res)

	if (!res.ok) {
		throw new HttpError(res.status, parsed, `HTTP ${res.status} ${res.statusText} for ${path}`)
	}

	return parsed as T
}
